import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LearnMore } from "@/components/resources/LearnMore";
import { toast } from "sonner";

import { VerificationGate } from "@/components/payments/VerificationGate";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

/*
 * Verification gate (event's L1-L4 level) -> terms acceptance -> bidding
 * registration -> paddle number. Public events: instant paddle. Private
 * events / L4: request lands with the organizer.
 */
export const RegisterToBidDialog = ({ open, onOpenChange, eventId, eventVisibility, terms, requiredLevel = 1, onRegistered }) => {
  const { t, i18n } = useTranslation();
  const [acceptPlatform, setAcceptPlatform] = useState(false);
  const [acceptAuction, setAcceptAuction] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tsToken, setTsToken] = useState(null);
  const [gateOk, setGateOk] = useState(requiredLevel <= 0);
  const [checkedLevel, setCheckedLevel] = useState(null);

  useEffect(() => {
    if (!open) return;
    api
      .get("/verification/me")
      .then(({ data }) => {
        setCheckedLevel(data.level);
        setGateOk(data.level >= Math.min(requiredLevel, 3));
      })
      .catch(() => setGateOk(true)); // fail open in UI — the backend still enforces
  }, [open, requiredLevel]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/register`, {
        terms_version: terms?.version || 1,
        // Legal Engine: explicit, versioned acceptance recorded server-side,
        // including the exact language version shown to the user
        accept_platform_terms: acceptPlatform,
        accept_auction_terms: acceptAuction,
        locale: i18n.resolvedLanguage,
        turnstile_token: tsToken,
      });
      setResult(data);
      onRegistered?.(data);
    } catch (err) {
      if (err?.response?.status === 403 && err.response.data?.detail?.code === "verification_required") {
        setGateOk(false); // backend says not enough — show the gate
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError"));
      }
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setAcceptPlatform(false);
      setAcceptAuction(false);
      setResult(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="register-dialog">
        {result ? (
          <div className="flex flex-col items-center text-center gap-4 py-6" data-testid="register-result">
            {result.status === "approved" ? (
              <>
                <span className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-[#1D4D3B] text-white text-2xl font-semibold">
                  #{result.paddle_number}
                </span>
                <DialogTitle className="text-2xl font-semibold tracking-tight" data-testid="register-paddle-title">
                  {t("bid.paddleAssigned", { paddle: result.paddle_number })}
                </DialogTitle>
                <Button
                  onClick={() => close(false)}
                  data-testid="register-done"
                  className="rounded-full bg-[#0A0A0A] text-white px-7 hover:bg-[#2b2b2b]"
                >
                  {t("bid.startBidding")}
                </Button>
              </>
            ) : (
              <>
                <DialogTitle className="text-2xl font-semibold tracking-tight">{t("bid.pendingTitle")}</DialogTitle>
                <DialogDescription className="text-sm text-[#737373]">{t("bid.pendingDesc")}</DialogDescription>
                <Button onClick={() => close(false)} variant="outline" className="rounded-full px-7" data-testid="register-pending-close">
                  {t("common.close")}
                </Button>
              </>
            )}
          </div>
        ) : !gateOk ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold tracking-tight">{t("vrf.dialogTitle")}</DialogTitle>
              <DialogDescription className="text-sm text-[#737373]">
                {t("vrf.dialogDesc", { level: requiredLevel })}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <VerificationGate requiredLevel={requiredLevel} onSatisfied={() => setGateOk(true)} />
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold tracking-tight">{t("bid.termsTitle")}</DialogTitle>
              <LearnMore slug="registering-and-verifying-your-account" section="auction-registration" className="w-fit" />
              <DialogDescription className="text-sm text-[#737373]">
                {eventVisibility === "public" ? t("bid.termsDescPublic") : t("bid.privateEvent")}
              </DialogDescription>
            </DialogHeader>
            {/* Legal Engine: versioned Platform + Auction Terms — each opens in a new tab */}
            <p className="text-xs text-[#737373] leading-relaxed">{t("bid.legalIntro")}</p>
            <div className="flex flex-col gap-3" data-testid="register-terms-body">
              {[
                { key: "platform", checked: acceptPlatform, set: setAcceptPlatform, label: t("bid.acceptPlatform"), href: "/legal/terms" },
                { key: "auction", checked: acceptAuction, set: setAcceptAuction, label: t("bid.acceptAuction"), href: `/auction-terms/${eventId}` },
              ].map((d) => (
                <div key={d.key} className="rounded-2xl border border-black/10 px-4 py-3.5 flex items-start gap-3" data-testid={`register-legal-row-${d.key}`}>
                  <Checkbox
                    checked={d.checked}
                    onCheckedChange={(v) => d.set(Boolean(v))}
                    data-testid={`register-accept-${d.key}`}
                    className="mt-0.5"
                    aria-label={d.label}
                  />
                  <button type="button" onClick={() => d.set(!d.checked)} className="flex-1 text-left text-sm text-[#0A0A0A] leading-snug">
                    {d.label}
                  </button>
                  <a
                    href={d.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`register-view-${d.key}`}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#1D4D3B] hover:underline underline-offset-4"
                  >
                    {t("bid.viewDocument")}
                    <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
                  </a>
                </div>
              ))}
            </div>
            <TurnstileWidget onToken={setTsToken} />
            <Button
              onClick={submit}
              disabled={!acceptPlatform || !acceptAuction || busy}
              data-testid="register-submit"
              className="rounded-full bg-[#0A0A0A] text-white h-11 hover:bg-[#2b2b2b] disabled:opacity-40"
            >
              {busy ? "…" : eventVisibility === "public" ? t("bid.registerToBid") : t("bid.requestAccess")}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
